import React, { Component } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import FeatherIcon from "feather-icons-react";
import * as XLSX from "xlsx";
import StudentForm from "./StudentForm";
import Listprint from "../../Listprint";

class StudentIdentificationView extends Component {
    state = {
        isModal: false, formData: "", classe: "", students: [], loading: true, loadError: false,
        columns: [
            { label: "Matricule", field: "matricule", sort: "asc", width: 90 },
            { label: "Élève", field: "student", sort: "asc", width: 210 },
            { label: "Genre", field: "genre", sort: "asc", width: 70 },
            { label: "Classe", field: "class", sort: "asc", width: 90 },
            { label: "Lieu et date de naissance", field: "adress", sort: "asc", width: 190 },
            { label: "Père", field: "father", sort: "asc", width: 160 },
            { label: "Profession", field: "fatherWork", sort: "asc", width: 130 },
            { label: "Mère", field: "monther", sort: "asc", width: 160 },
            { label: "Profession", field: "montherWork", sort: "asc", width: 130 },
            { label: "Téléphone", field: "tel", sort: "asc", width: 150 },
            { label: "Quartier", field: "quartier", sort: "asc", width: 120 },
            { label: "Commune / Groupement", field: "groupment", sort: "asc", width: 160 },
            { label: "Territoire", field: "territory", sort: "asc", width: 120 },
            { label: "Dossier", field: "action", sort: "disabled", width: 75, printable: false }
        ]
    };

    componentDidMount() { this.getStudents(); }

    getStudents = async () => {
        this.setState({ loading: true, loadError: false });
        try {
            const response = await axios.get("students");
            this.setState({ students: response.status === 200 && Array.isArray(response.data) ? response.data : [], loading: false });
        } catch (error) { this.setState({ loading: false, loadError: true }); }
    };

    showModal = (student = null, classe = false) => {
        this.setState((state) => ({ isModal: !state.isModal, formData: student, classe: classe ? student : null }), () => {
            if (!this.state.isModal) this.getStudents();
        });
    };

    handleCheck = (permission) => {
        const permissions = localStorage.getItem("permissions") ? JSON.parse(localStorage.getItem("permissions")) : [];
        return permissions.some((item) => permission === item.permission);
    };

    formatStudent = (student) => {
        const birthDate = student.user?.datebirth ? new Date(student.user.datebirth).toLocaleDateString("fr-FR") : "";
        return {
            rawMatricule: student.matricule || "",
            matricule: student.user ? student.matricule || "—" : "—",
            student: [student.user?.last_name, student.user?.family_name, student.user?.first_name].filter(Boolean).join(" ") || "Nom non renseigné",
            genre: student.user?.genre || "—",
            class: [student.sectionclass?.class?.short_entitled_class, student.sectionclass?.section?.short_entitled_section].filter(Boolean).join(" ") || "—",
            adress: [student.user?.adress, birthDate].filter(Boolean).join(", ") || "—",
            father: [student.father?.first_name, student.father?.last_name].filter(Boolean).join(" ") || "—",
            fatherWork: student.father?.profession || "—",
            monther: [student.monther?.first_name, student.monther?.last_name].filter(Boolean).join(" ") || "—",
            montherWork: student.monther?.profession || "—",
            tel: [student.monther?.tel, student.father?.tel].filter(Boolean).join(", ") || "—",
            quartier: student.quartier?.name || "—",
            groupment: [student.commune?.name, student.groupment?.name].filter(Boolean).join(", ") || "—",
            territory: student.territory?.name || "—"
        };
    };

    getSortedStudents = () => [...this.state.students].sort((a, b) => {
        const classA = a.sectionclass?.class?.short_entitled_class || "";
        const classB = b.sectionclass?.class?.short_entitled_class || "";
        if (classA !== classB) return classA.localeCompare(classB, "fr", { numeric: true });
        const nameA = [a.user?.last_name, a.user?.family_name, a.user?.first_name].filter(Boolean).join(" ");
        const nameB = [b.user?.last_name, b.user?.family_name, b.user?.first_name].filter(Boolean).join(" ");
        return nameA.localeCompare(nameB, "fr");
    });

    getRows = () => this.getSortedStudents().map((student) => {
        const data = this.formatStudent(student);
        return {
            id: student.id, ...data,
            matricule: <Link to={`/students/${student.id}`} className="identification-matricule">{data.matricule}</Link>,
            action: this.handleCheck("Afficher élève") ? <Link to={`/studentfile/${student.id}`} className="identification-file-button" title="Ouvrir le dossier" aria-label={`Ouvrir le dossier de ${data.student}`}><FeatherIcon icon="file-text" size="15" /></Link> : "—"
        };
    });

    handleExport = () => {
        const data = this.getSortedStudents().map((student) => {
            const row = this.formatStudent(student);
            return {
                Matricule: row.rawMatricule, Élève: row.student, Genre: row.genre, Classe: row.class,
                "Lieu et date de naissance": row.adress, Père: row.father, "Profession du père": row.fatherWork,
                Mère: row.monther, "Profession de la mère": row.montherWork, Téléphone: row.tel,
                Quartier: row.quartier, "Commune / Groupement": row.groupment, Territoire: row.territory
            };
        });
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Identification");
        const date = new Date().toLocaleDateString("fr-FR").replaceAll("/", "-");
        XLSX.writeFile(workbook, `fiche_identification_eleves_${date}.xlsx`);
    };

    render() {
        const rows = this.getRows();
        const sections = new Set(this.state.students.map((student) => student.sectionclass?.section?.id).filter(Boolean)).size;
        const classes = new Set(this.state.students.map((student) => student.sectionclass?.id).filter(Boolean)).size;
        const documented = this.state.students.filter((student) => student.user?.datebirth && (student.father || student.monther) && student.quartier).length;
        return <main className="identification-page">
            <Helmet><title>{process.env.REACT_APP_NAME} | Identification des élèves</title></Helmet>
            <header className="identification-header">
                <div className="identification-header__seal" aria-hidden="true"><FeatherIcon icon="clipboard" size="27" /></div>
                <div className="identification-header__copy"><span className="identification-header__eyebrow">Administration scolaire</span><h1>Fiche d’identification des élèves</h1><p>Registre central des renseignements personnels, familiaux et administratifs.</p></div>
                <div className="identification-header__actions">
                    {this.handleCheck("Ajouter élève") && <button type="button" className="btn identification-button identification-button--light" onClick={() => this.showModal(null, true)}><FeatherIcon icon="user-plus" size="17" /> Nouvel élève</button>}
                    <button type="button" className="btn identification-button identification-button--outline" onClick={this.handleExport} disabled={!this.state.students.length}><FeatherIcon icon="download" size="17" /> Exporter Excel</button>
                </div>
            </header>
            {!this.state.loading && !this.state.loadError && <section className="identification-stats" aria-label="Résumé du registre">
                <article className="identification-stat"><span className="identification-stat__icon"><FeatherIcon icon="users" size="20" /></span><div><strong>{this.state.students.length}</strong><span>Élèves enregistrés</span></div></article>
                <article className="identification-stat"><span className="identification-stat__icon identification-stat__icon--blue"><FeatherIcon icon="book-open" size="20" /></span><div><strong>{classes}</strong><span>Classes représentées</span></div></article>
                <article className="identification-stat"><span className="identification-stat__icon identification-stat__icon--purple"><FeatherIcon icon="layers" size="20" /></span><div><strong>{sections}</strong><span>Sections scolaires</span></div></article>
                <article className="identification-stat"><span className="identification-stat__icon identification-stat__icon--green"><FeatherIcon icon="check-circle" size="20" /></span><div><strong>{documented}</strong><span>Dossiers renseignés</span></div></article>
            </section>}
            {this.state.loading && <div className="identification-feedback"><FeatherIcon className="user-search-spin" icon="loader" /></div>}
            {!this.state.loading && this.state.loadError && <div className="identification-feedback identification-feedback--error"><FeatherIcon icon="alert-circle" size="30" /><h2>Impossible de charger le registre</h2><p>Vérifiez votre connexion puis relancez le chargement.</p><button type="button" className="btn btn-sm btn-primary" onClick={this.getStudents}><FeatherIcon icon="refresh-cw" size="15" /> Réessayer</button></div>}
            {!this.state.loading && !this.state.loadError && <section className="identification-register">
                <div className="identification-register__heading"><div><span className="identification-register__icon"><FeatherIcon icon="archive" size="19" /></span><div><h2>Registre d’identification</h2><p>Informations classées par classe et nom de l’élève</p></div></div><span className="identification-register__badge">Document administratif</span></div>
                {rows.length ? <div className="identification-register__table"><Listprint columns={this.state.columns} rows={rows} title="Fiche d’identification des élèves" /></div> : <div className="identification-empty"><span><FeatherIcon icon="folder" size="29" /></span><h2>Le registre est encore vide</h2><p>Les élèves ajoutés apparaîtront ici avec leurs informations administratives.</p></div>}
            </section>}
            {this.state.isModal && <StudentForm isModal={this.state.isModal} formData={this.state.formData} classe={this.state.classe} matricule={(this.state.students.length + 1)+"" + (this.state.students[0]?.year?.id || '')} showModal={this.showModal} />}
        </main>;
    }
}

export default StudentIdentificationView;
